import axios from 'axios'
import {
  resolveTokenExpiry,
  computeRefreshDelay,
  isTokenUsable,
} from '../utils/connection'

// Token de staff (JWT RS256, ~10 min) emitido pelo backend-core para falar com o
// meufuturo-chat-service via api-gateway. Fica SÓ em memória (nunca localStorage):
// some ao recarregar a página e é limpo no logout.

export const CHAT_ERROR_UNAVAILABLE = 'CHAT_UNAVAILABLE'
export const CHAT_ERROR_FORBIDDEN = 'CHAT_FORBIDDEN'
export const CHAT_ERROR_SESSION_CLOSED = 'CHAT_SESSION_CLOSED'
export const CHAT_ERROR_UNKNOWN = 'CHAT_UNKNOWN'

const TOKEN_PATH = '/api/customer-chat/token'

const createChatError = (code, cause) => {
  const error = new Error(code)
  error.code = code
  error.status = cause && cause.response ? cause.response.status : undefined
  error.cause = cause
  return error
}

// Instância própria para o backend-core, reaproveitando baseURL e Authorization
// (Sanctum) que o repo já configura em `axios.defaults` (helpers/api.js e a mutation
// MUTATE_TOKEN). Não usamos a instância global diretamente porque o interceptor dela
// desloga o usuário em qualquer 403 — e aqui 403 só significa "sem acesso ao chat".
export const coreClient = axios.create()
coreClient.interceptors.request.use(config => {
  const next = config
  next.baseURL = axios.defaults.baseURL
  const common = axios.defaults.headers.common || {}
  if (common.Authorization) next.headers.Authorization = common.Authorization
  next.headers['X-Requested-With'] = 'XMLHttpRequest'
  return next
})

let current = null
let inflight = null
let refreshTimer = null
// Incrementado no logout: respostas que chegarem depois são descartadas.
let generation = 0

const clearRefreshTimer = () => {
  if (refreshTimer) clearTimeout(refreshTimer)
  refreshTimer = null
}

const mapError = error => {
  if (error && error.code && error.code.startsWith('CHAT_')) return error
  const status = error && error.response ? error.response.status : undefined
  if (status === 503) return createChatError(CHAT_ERROR_UNAVAILABLE, error)
  if (status === 403) return createChatError(CHAT_ERROR_FORBIDDEN, error)
  return createChatError(CHAT_ERROR_UNKNOWN, error)
}

let scheduleRefresh = () => {}

const fetchToken = () => {
  const requestGeneration = generation
  return coreClient.post(TOKEN_PATH)
    .then(({ data }) => {
      if (requestGeneration !== generation) throw createChatError(CHAT_ERROR_SESSION_CLOSED)
      if (!data || !data.token || !data.apiBaseUrl) throw createChatError(CHAT_ERROR_UNKNOWN)
      const now = Date.now()
      current = {
        token: data.token,
        expiresAtMs: resolveTokenExpiry(data, now),
        apiBaseUrl: String(data.apiBaseUrl).replace(/\/+$/, ''),
        wsUrl: data.wsUrl || null,
      }
      scheduleRefresh()
      return current
    })
    .catch(error => Promise.reject(mapError(error)))
}

/**
 * Devolve um token válido. Chamadas concorrentes compartilham a mesma requisição.
 * `forceRefresh` ignora o token em memória (ex.: após 401 do chat).
 */
export const getStaffToken = ({ forceRefresh = false } = {}) => {
  if (!forceRefresh && current && isTokenUsable(current.expiresAtMs, Date.now())) {
    return Promise.resolve(current)
  }
  if (!inflight) {
    inflight = fetchToken().finally(() => {
      inflight = null
    })
  }
  return inflight
}

scheduleRefresh = () => {
  clearRefreshTimer()
  if (!current) return
  const delay = computeRefreshDelay(current.expiresAtMs, Date.now())
  refreshTimer = setTimeout(() => {
    refreshTimer = null
    // Falha aqui não é fatal: a próxima chamada tenta de novo sob demanda.
    getStaffToken({ forceRefresh: true }).catch(() => {})
  }, delay)
}

export const getCachedConnectionInfo = () => (current ? { apiBaseUrl: current.apiBaseUrl, wsUrl: current.wsUrl } : null)

export const clearStaffToken = () => {
  generation += 1
  clearRefreshTimer()
  current = null
  inflight = null
}
